import { useMemo } from 'react';
import type { ComponentView, StoreInstallationView, StoreWebsiteView } from '../../api';
import { api } from '../../api';
import { useResource } from '../../hooks';
import { publicDomainOf } from './labels';

/**
 * usePublicDomains reads the components of every environment the stores
 * run in, so each website can be shown with the domain shoppers reach it on
 * (CO-158). An environment whose components cannot be read leaves its
 * websites' domains not known rather than failing the page.
 */
export function usePublicDomains(stores: StoreInstallationView[]) {
  const places = useMemo(
    () => [...new Set(stores.map((store) => `${store.project_id}:${store.environment_id}`))].sort(),
    [stores],
  );
  const joined = places.join(',');
  const components = useResource(
    () =>
      Promise.all(
        places.map((place) => {
          const split = place.indexOf(':');
          const projectId = place.slice(0, split);
          const environmentId = place.slice(split + 1);
          return api
            .getComponentsOverview(projectId, environmentId)
            .then((overview) => [place, overview.components] as const)
            .catch(() => [place, null] as const);
        }),
      ).then((entries) => Object.fromEntries(entries) as Record<string, ComponentView[] | null>),
    [joined],
    joined,
    { key: `erp:public-domains:${joined}`, enabled: places.length > 0 },
  );
  const byPlace = components.data;
  return useMemo(
    () => ({
      loading: components.loading && !byPlace,
      // Without the components it is not known whether a frontend serves
      // the website, so the store's own address is not offered instead.
      domainOf: (store: StoreInstallationView, website: Pick<StoreWebsiteView, 'code' | 'base_urls'>) => {
        const list = byPlace?.[`${store.project_id}:${store.environment_id}`];
        return list ? publicDomainOf(store, website, list) : null;
      },
    }),
    [byPlace, components.loading],
  );
}
